"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Link } from "@/components/common/Link";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, Calendar, User, Tag, ChevronLeft, ChevronRight } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function PostSkeleton() {
  return <div className="flex flex-col gap-4">
      <div className="aspect-video w-full bg-muted animate-pulse rounded-lg" />
      <div className="h-6 w-3/4 bg-muted animate-pulse rounded" />
      <div className="h-4 w-full bg-muted animate-pulse rounded" />
    </div>;
}
function ListSkeleton() {
  return <div className="flex flex-col gap-4">
      {[...Array(3)].map((_, i) => <div key={i} className="flex gap-4 items-start">
          <div className="size-20 bg-muted animate-pulse rounded-md shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-full bg-muted animate-pulse rounded" />
            <div className="h-3 w-1/2 bg-muted animate-pulse rounded" />
          </div>
        </div>)}
    </div>;
}
function PaginationControls({
  hasNext,
  hasPrev,
  nextPage,
  prevPage,
  currentPage,
  totalPages
}: {
  hasNext: boolean;
  hasPrev: boolean;
  nextPage: () => void;
  prevPage: () => void;
  currentPage: number;
  totalPages: number;
}) {
  if (totalPages <= 1) return null;
  return <div className="flex items-center justify-center gap-4 mt-6">
      <Button variant="outline" size="sm" onClick={prevPage} disabled={!hasPrev}>
        <ChevronLeft className="size-4 mr-1" /> Sebelumnya
      </Button>
      <span className="text-sm text-muted-foreground">
        Halaman {currentPage} dari {totalPages}
      </span>
      <Button variant="outline" size="sm" onClick={nextPage} disabled={!hasNext}>
        Berikutnya <ChevronRight className="size-4 ml-1" />
      </Button>
    </div>;
}
function OpinionConsumer() {
  const {
    posts,
    loading,
    error,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    currentPage,
    totalPages
  } = usePosts();
  if (loading) return <div className="flex flex-col gap-8"><PostSkeleton /><ListSkeleton /></div>;
  if (error) return <div className="p-8 text-center text-muted-foreground border rounded-lg">Gagal memuat opini.</div>;
  if (posts.length === 0) return <div className="p-8 text-center text-muted-foreground border rounded-lg">Tidak ada opini tersedia.</div>;
  const featured = posts[0];
  const list = posts.slice(1);
  return <div className="flex flex-col gap-4 md:gap-6">
      <Card className="overflow-hidden border-border shadow-sm p-0 gap-0 group">
        <Link to={featured.link || "#"} className="block">
          <div className="relative overflow-hidden aspect-square sm:aspect-video">
            <img src={featured.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1580001/img/placeholder_news.png"} data-wvc-srcset={featured.featuredImage ? `${featured.featuredImage}-wvc-srcset` : ""} data-wvc-sizes={featured.featuredImage ? `${featured.featuredImage}-wvc-sizes` : ""} alt={featured.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
          </div>
          <CardContent className="p-6">
            <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3">
              <span className="flex items-center gap-1 text-primary font-medium">
                <User className="size-3.5" />
                {featured.author?.name || "Redaksi"}
              </span>
              <span className="flex items-center gap-1">
                <SafeHydrate><Calendar className="size-3.5" /></SafeHydrate>
                {featured.date ? new Date(featured.date).toLocaleDateString('id-ID', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              }) : ""}
              </span>
            </div>
            <h3 className="text-xl font-bold text-foreground mb-3 group-hover:text-primary transition-colors line-clamp-2 md:line-clamp-3">
              {featured.title}
            </h3>
            <p className="text-muted-foreground text-sm line-clamp-2" dangerouslySetInnerHTML={{
            __html: featured.excerpt || ""
          }} />
          </CardContent>
        </Link>
      </Card>

      <div className="flex flex-col gap-4">
        {list.map((item, i) => <Link key={item.id} to={item.link || "#"} data-index={i} className="group flex gap-4 items-start p-2 -mx-2 rounded-lg hover:bg-muted/50 transition-colors">
            <div className="relative w-20 h-20 sm:w-24 sm:h-24 shrink-0 overflow-hidden rounded-md">
              <img src={item.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1580001/img/placeholder_news.png"} data-wvc-srcset={item.featuredImage ? `${item.featuredImage}-wvc-srcset` : ""} data-wvc-sizes={item.featuredImage ? `${item.featuredImage}-wvc-sizes` : ""} alt={item.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
            </div>
            <div className="flex flex-col justify-center min-h-[4rem] md:min-h-[6rem]">
              <h4 className="font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2 mb-2">
                {item.title}
              </h4>
              <div className="flex items-center gap-3 text-xs text-muted-foreground mt-auto">
                <span className="text-primary font-medium">{item.author?.name || "Redaksi"}</span>
                <span>{item.date ? new Date(item.date).toLocaleDateString('id-ID', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              }) : ""}</span>
              </div>
            </div>
          </Link>)}
      </div>

      <PaginationControls hasNext={hasNext} hasPrev={hasPrev} nextPage={nextPage} prevPage={prevPage} currentPage={currentPage} totalPages={totalPages} />
    </div>;
}
function FeatureConsumer() {
  const {
    posts,
    loading,
    error,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    currentPage,
    totalPages
  } = usePosts();
  if (loading) return <div className="flex flex-col gap-8"><PostSkeleton /><ListSkeleton /></div>;
  if (error) return <div className="p-8 text-center text-muted-foreground border rounded-lg">Gagal memuat feature.</div>;
  if (posts.length === 0) return <div className="p-8 text-center text-muted-foreground border rounded-lg">Tidak ada feature tersedia.</div>;
  const featured = posts[0];
  const list = posts.slice(1);
  return <div className="flex flex-col gap-4 md:gap-6">
      <Card className="overflow-hidden border-border shadow-sm p-0 gap-0 group">
        <Link to={featured.link || "#"} className="block">
          <div className="relative overflow-hidden aspect-square sm:aspect-video">
            <img src={featured.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1580001/img/placeholder_news.png"} data-wvc-srcset={featured.featuredImage ? `${featured.featuredImage}-wvc-srcset` : ""} data-wvc-sizes={featured.featuredImage ? `${featured.featuredImage}-wvc-sizes` : ""} alt={featured.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
            <div className="absolute top-4 left-4 bg-primary text-primary-foreground text-xs font-bold px-2.5 py-1 rounded-md uppercase tracking-wider">
              {featured.categories?.[0]?.name || "Feature"}
            </div>
          </div>
          <CardContent className="p-6">
            <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3">
              <span className="flex items-center gap-1">
                <SafeHydrate><Calendar className="size-3.5" /></SafeHydrate>
                {featured.date ? new Date(featured.date).toLocaleDateString('id-ID', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              }) : ""}
              </span>
            </div>
            <h3 className="text-xl font-bold text-foreground mb-3 group-hover:text-primary transition-colors line-clamp-2 md:line-clamp-3">
              {featured.title}
            </h3>
            <p className="text-muted-foreground text-sm line-clamp-2" dangerouslySetInnerHTML={{
            __html: featured.excerpt || ""
          }} />
          </CardContent>
        </Link>
      </Card>

      <div className="flex flex-col gap-4">
        {list.map((item, i) => <Link key={item.id} to={item.link || "#"} data-index={i} className="group flex gap-4 items-start p-2 -mx-2 rounded-lg hover:bg-muted/50 transition-colors">
            <div className="relative w-20 h-20 sm:w-24 sm:h-24 shrink-0 overflow-hidden rounded-md">
              <img src={item.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1580001/img/placeholder_news.png"} data-wvc-srcset={item.featuredImage ? `${item.featuredImage}-wvc-srcset` : ""} data-wvc-sizes={item.featuredImage ? `${item.featuredImage}-wvc-sizes` : ""} alt={item.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
            </div>
            <div className="flex flex-col justify-center min-h-[4rem] md:min-h-[6rem]">
              <h4 className="font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2 mb-2">
                {item.title}
              </h4>
              <div className="flex items-center gap-3 text-xs text-muted-foreground mt-auto">
                <span className="flex items-center gap-1 text-primary font-medium uppercase tracking-wider text-[10px]">
                  <Tag className="size-3" />
                  {item.categories?.[0]?.name || "Feature"}
                </span>
                <span>{item.date ? new Date(item.date).toLocaleDateString('id-ID', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              }) : ""}</span>
              </div>
            </div>
          </Link>)}
      </div>

      <PaginationControls hasNext={hasNext} hasPrev={hasPrev} nextPage={nextPage} prevPage={prevPage} currentPage={currentPage} totalPages={totalPages} />
    </div>;
}
export default function OpinionAndFeature() {
  const opinionQuery = new WP_Query({
    post_type: "post",
    per_page: 4,
    orderby: "date",
    order: "desc",
    status: "publish"
  });
  const featureQuery = new WP_Query({
    post_type: "post",
    per_page: 4,
    orderby: "date",
    order: "desc",
    status: "publish"
  });
  return <section id="opini-feature" className="py-16 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-12">
          
          <div className="flex flex-col gap-8">
            <div className="flex items-center justify-between border-b-2 border-primary pb-2">
              <h2 className="text-2xl font-bold text-foreground uppercase tracking-wider">
                Opini
              </h2>
              <Button variant="ghost" size="sm" asChild className="hidden sm:flex text-muted-foreground hover:text-primary">
                <Link to="/OpinionPage">
                  Lihat Semua <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>
            </div>

            <PostsProvider wp_query={{
            post_type: "post",
            per_page: 4,
            orderby: "date",
            order: "desc",
            status: ["publish"]
          }}>
              <OpinionConsumer />
            </PostsProvider>
            
            <Button variant="outline" className="w-full sm:hidden mt-2" asChild>
              <Link to="/OpinionPage">Lihat Semua Opini</Link>
            </Button>
          </div>

          <div className="flex flex-col gap-8">
            <div className="flex items-center justify-between border-b-2 border-primary pb-2">
              <h2 className="text-2xl font-bold text-foreground uppercase tracking-wider">
                Feature & Wawancara
              </h2>
              <Button variant="ghost" size="sm" asChild className="hidden sm:flex text-muted-foreground hover:text-primary">
                <Link to="/NewsPage">
                  Lihat Semua <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>
            </div>

            <PostsProvider wp_query={{
            post_type: "post",
            per_page: 4,
            orderby: "date",
            order: "desc",
            status: ["publish"]
          }}>
              <FeatureConsumer />
            </PostsProvider>

            <Button variant="outline" className="w-full sm:hidden mt-2" asChild>
              <Link to="/NewsPage">Lihat Semua Feature</Link>
            </Button>
          </div>

        </div>
      </div>
    </section>;
}